/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { KEYCODE } from '../../utils/keycode.js';

/**
 * @prop {String} src - Necessary field, defines the source location of the image data.
 * @prop {String} label - Default Label. Defines the label for the image data. (Optional)
 * @prop {String} number - Default 1. Defines the number associated with the image data. (Optional)
 * @prop {String} icon - Default series. Defines the type icon that will be displayed on the thumbnail in the bottom-right. (Optional)
 * @prop {Boolean} active - Default false. Toggles the active state of the thumbnail.
 * @prop {Boolean} new - Default false. Toggles the new state of the thumbnail. This is a deprecated property and is replaced by the badge property.
 * @prop {String} badge - Default new. Defines the badge that will be shown in the top right corner of the thumbnail. Accepted values are newand warning.
 * @prop {Boolean} focus - Default false. Toggles the focus state of the thumbnail.
 * @prop {Boolean} selectable - Default false. Makes the thumbnail component selectable by displaying a checkbox control on the top left. The checkbox will always be shown in case of touch devices and only shown on hover when a pointing device is available. (Optional)
 * @prop {String} aspectRatio - Default 1/1. Makes the thumbnail support different image aspect ratios, for e.g. 4/3. (Optional)
 * @prop {Boolean} options - Shows options menu if enabled.
 * @prop {Boolean} show - *hide used internally
 * @attr {String} aspect-ratio - Default 1/1. Makes the thumbnail support different image aspect ratios, for e.g. 4/3. (Optional)
 * @prop {Boolean} indeterminate - Default false. Toggles the indeterminate state of the thumbnail and displays a 'minus' icon in the checkbox when checked on the thumbnail.
 * @prop {Boolean} checked - Default false. Toggles the checked state of the thumbnail and displays a selected checkbox on the thumbnail.
 * @slot icon - Slot for displaying additional icons, displayed as row below the image. Takes icons as children.
 * @slot info - Slot for displaying metadata associated with the dataset. Takes children such as text.
 * @slot data-type - Slot for displaying icons for the type-of or origin of the dataset. Displayed on the bottom-right of the thumbnail. Using this slot will override the icon displayed uding the icon property. Takes icons as children.
 * @slot indications - Slot for displaying icons related to the status of a thumbnail, or attributes of the thumbnail relevant to a user. Displayed on the top-right of the thumbnail. Takes icons as children.
 * @fires menu-clicked - Dispatched when the menu(options) icon is clicked.
 * @prop {Boolean} condensed - Default false. Changes design to a condensed version.
 * @prop {String} discard - Displays a thumbnail as discarded. The thumbnail is shown with a black overlay, with thumbnail text shown as disabled. By default no icon is shown, but other icons can be shown by defining the icon name as a string for the property.
 * @prop {Boolean} loading - Displays a skeleton structure of a thumbnail which can be used as a placeholder while the image and the thumbnail metadata are being loaded.
 */

export class SHThumbnail extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      src: { type: String, reflect: true },
      number: { type: Number, reflect: true },
      active: { type: Boolean, reflect: true },
      focus: { type: Boolean, reflect: true },
      selectable: { type: Boolean, reflect: true },
      new: { type: Boolean, reflect: true },
      badge: { type: String, reflect: true },
      checked: { type: Boolean, reflect: true },
      indeterminate: { type: Boolean, reflect: true },
      options: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      icon: { type: String, reflect: true },
      aspectRatio: { type: String, reflect: true, attribute: 'aspect-ratio' },
      show: { type: Boolean, reflect: true },
      discard: { type: String, reflect: true },
      loading: { type: Boolean, reflect: true },
      _emptyInfo: { type: Boolean },
      _emptyDataTypeIcons: { type: Boolean },
      _emptyIndications: { type: Boolean },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          display: block;
          height: auto;
          transition: var(--transition-time) all ease-in-out;
          position: relative;
          border-radius: 0.125rem;
          border: 0.0625rem solid transparent;
        }

        :host([selectable]:not([checked]):not(:hover)) .thumbnail-checkbox,
        .thumbnail-wrapper:not([empty-data-type]) .thumbnail-icon {
          display: none;
        }
        .thumbnail-wrapper {
          display: block;
          cursor: pointer;
          color: rgba(var(--ui-1), var(--opacity-3));
          position: relative;
        }
        .image-wrapper {
          display: flex;
          flex-direction: column;
          padding: 0.5rem 0.5rem 0.5rem 0.5rem;
        }
        :host([condensed]) .image-wrapper {
          padding: 0.25rem 0.25rem 0.25rem 0.25rem;
        }
        .image {
          background-size: contain;
          background-color: black;
          background-position: center;
          background-repeat: no-repeat;
          width: 100%;
          padding-top: 100%;
          box-sizing: border-box;
          transition: var(--transition-time) all ease-in-out;
          border-radius: 0.125rem;
        }
        .label {
          position: relative;
          overflow: hidden;
          word-break: break-all;
          text-overflow: ellipsis;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          flex: 1;
          height: 2rem;
          line-height: 1rem;
        }
        .label-wrapper {
          height: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: auto;
          color: rgba(var(--ui-1), var(--opacity-3));
          margin: -0.25rem 0.5rem 0.25rem 0.5rem;
        }
        :host([condensed]) .label-wrapper {
          margin: 0rem 0.25rem 0.25rem 0.25rem;
        }
        :host([selectable].no-hovermq) .thumbnail-checkbox,
        :host([selectable].no-hovermq:not([checked]):not(:hover)) .thumbnail-checkbox,
        :host([selectable]:hover) .thumbnail-checkbox,
        :host([checked]) .thumbnail-checkbox {
          display: inline-block;
          position: absolute;
          top: 0.625rem;
          left: 0.625rem;
          width: 1.5rem;
          padding: 0.25rem;
        }
        :host([selectable][condensed].no-hovermq) .thumbnail-checkbox,
        :host([selectable][condensed].no-hovermq:not([checked]):not(:hover)) .thumbnail-checkbox,
        :host([selectable][condensed]:hover) .thumbnail-checkbox,
        :host([checked][condensed]) .thumbnail-checkbox {
          top: 0.375rem;
          left: 0.375rem;
        }
        :host([selectable][discard]) .thumbnail-checkbox {
          z-index: 1;
        }
        .thumbnail-checkbox::before,
        .thumbnail-icon::before {
          height: 2rem;
          width: 2rem;
          top: 0rem;
          left: 0rem;
          content: '';
          background: var(--base-2);
          opacity: var(--opacity-2);
          position: absolute;
          border-radius: 0.0625rem;
        }
        .thumbnail-icon::before {
          background: rgba(0, 0, 0, var(--opacity-3));
        }
        :host(:not([loading]):hover) {
          background: rgba(var(--ui-1), var(--opacity-7));
        }
        :host(:not([loading])[active]) {
          background: rgba(var(--ui-1), var(--opacity-6));
        }
        :host([active]) .label,
        :host([focus]) .label {
          color: rgba(var(--ui-1), var(--opacity-2));
        }
        :host(:not([loading])[focus]) {
          border-color: rgb(var(--ui-0));
        }

        /* badge slot */
        :host([badge]) .thumbnail-badge {
          display: block;
          width: 1rem;
          margin: 0.5rem;
        }
        :host([badge='new']) .thumbnail-badge {
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 0.5rem;
          background-color: rgb(var(--ui-0));
        }
        :host([badge='warning']) .thumbnail-badge {
          margin: 0.25rem;
        }

        .thumbnail-icon {
          display: block;
          position: absolute;
          bottom: 0.625rem;
          right: 0.625rem;
          color: rgba(255, 255, 255, var(--opacity-3));
        }
        :host([condensed]) .thumbnail-icon {
          bottom: 0.375rem;
          right: 0.375rem;
        }

        /* icons slot */
        .icons-wrapper {
          display: flex;
        }

        .icons-wrapper ::slotted(*) {
          margin-left: 0.25rem;
          margin-bottom: 0.25rem;
          color: var(--text-secondary);
        }

        :host(:not([condensed])) .icons-wrapper ::slotted(*:first-child) {
          margin-left: 0.5rem;
        }

        /* badge */
        .new-icon {
          position: absolute;
          top: 0.625rem;
          right: 0.625rem;
          width: 0.5rem;
          margin: 0.5rem;
          height: 0.5rem;
          border-radius: 0.5rem;
          background-color: rgb(var(--ui-0));
        }
        :host([condensed]) .new-icon {
          top: 0.375rem;
          right: 0.375rem;
        }

        /* info slot */
        .info-slot-wrapper {
          display: flex;
          flex-direction: rows;
          display: block;
          position: absolute;
          bottom: 0.625rem;
          left: 0.625rem;
          background: var(--base-2);
          opacity: var(--opacity-3);
          padding: 0.25rem;
          border-radius: 0.125rem;
        }
        :host([condensed]) .info-slot-wrapper {
          bottom: 0.375rem;
          left: 0.375rem;
        }

        .thumbnail-wrapper[empty-info] .info-slot-wrapper {
          display: none;
        }

        :host([active]) .icons-wrapper ::slotted(*) {
          color: var(--text-primary);
        }

        /* data-type slot */
        .data-type-wrapper {
          display: flex;
          bottom: 0.5rem;
          right: 0.5rem;
          margin: 0 0.125rem 0.125rem 0;
          position: absolute;
        }

        .thumbnail-wrapper[empty-data-type] .data-type-wrapper {
          display: none;
        }

        .data-type-wrapper ::slotted(*),
        .indications-wrapper ::slotted(*) {
          background: rgb(0, 0, 0);
          color: rgb(255, 255, 255);
          opacity: var(--opacity-3);
          margin-left: 0.25rem;
          border-radius: 0.125rem;
          line-height: 1.75rem;
        }

        :host([condensed]) .data-type-wrapper {
          bottom: 0.25rem;
          right: 0.25rem;
        }

        /* indications slot */
        .indications-wrapper {
          display: flex;
          top: 0.5rem;
          right: 0.5rem;
          margin: 0.125rem 0.125rem 0 0;
          position: absolute;
        }

        :host([condensed]) .indications-wrapper {
          top: 0.25rem;
          right: 0.25rem;
        }
        sh-tooltip {
          max-width: 25rem;
          overflow-wrap: break-word;
        }
        sh-popover {
          pointer-events: all;
        }
        sh-popover:not([mobile]) {
          position: static;
        }
        :host(:not([show])) .options-popover {
          height: 0px;
        }
        sh-overlay {
          background: transparent;
        }
        .discard-icon {
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: rgba(255, 255, 255, var(--opacity-3));
        }
        :host([discard]) .label {
          color: var(--text-disabled);
        }
        :host([discard]) .id-number,
        :host([discard]) .icons-wrapper ::slotted(*),
        :host([discard]) .data-type-wrapper ::slotted(*),
        :host([discard]) .indications-wrapper ::slotted(*) {
          opacity: var(--opacity-4);
        }
        .discard-wrapper {
          position: absolute;
          width: calc(100% - 1rem);
          height: calc(100% - 1rem);
          background-color: rgba(0, 0, 0, var(--opacity-2));
          z-index: 1;
          display: flex;
        }
        .loading-wrapper {
          margin: 0.5rem;
          max-height: 100%;
        }
        .loading-image {
          border-radius: 0.125rem;
          margin-bottom: 0.25rem;
        }
        .loading-text {
          height: 1.5rem;
          width: 80%;
        }
        .loading {
          opacity: var(--opacity-5);
          background: linear-gradient(
              -70deg,
              rgba(var(--ui-1), var(--opacity-6)) 0%,
              rgba(var(--ui-1), var(--opacity-6)) 20%,
              rgba(var(--ui-1), var(--opacity-5)) 40%,
              rgba(var(--ui-1), var(--opacity-5)) 50%,
              rgba(var(--ui-1), var(--opacity-5)) 60%,
              rgba(var(--ui-1), var(--opacity-6)) 80%,
              rgba(var(--ui-1), var(--opacity-6)) 100%
            )
            repeat;
          background-size: 600% 200%;
          animation: gradient var(--animation-duration-thumbnail, 2s) ease-in-out infinite;
        }
        @keyframes gradient {
          0% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `,
    ];
  }

  render() {
    return html`
      ${!this.loading
        ? html`
            <div
              class="thumbnail-wrapper"
              id="thumbnailWrapper"
              ?empty-info="${this._emptyInfo}"
              ?empty-data-type="${this._emptyDataTypeIcons}"
              ?empty-indications="${this._emptyIndications}"
            >
              <div class="image-wrapper">
                <div
                  class="image"
                  style='background-image:url("${this.src
                    ? this.src
                    : ''}"); padding-top: calc(100% / (${this.aspectRatio}))'
                ></div>
                ${this.discard === '' || this.discard
                  ? html` <div class="discard-wrapper">
                      ${this.discard.length === 0
                        ? ''
                        : html` <sh-icon class="discard-icon" icon=${this.discard}></sh-icon>`}
                    </div>`
                  : ''}
                ${this.icon
                  ? html`<sh-icon class="thumbnail-icon" icon="${this.icon}"></sh-icon>`
                  : ''}
                <div class="data-type-wrapper">
                  <slot
                    name="data-type"
                    class="data-type"
                    @slotchange="${this._dataTypeSlotObserver}"
                  ></slot>
                </div>
                ${this.selectable
                  ? html` <sh-checkbox
                      label=""
                      class="thumbnail-checkbox"
                      ?active="${this.checked}"
                      @active-changed="${(e) => {
                        this.checked = e.target.active;
                      }}"
                      ?indeterminate="${this.indeterminate}"
                    ></sh-checkbox>`
                  : ''}
                ${this.new ? html`<div class="new-icon"></div>` : ''}
                <div class="indications-wrapper">
                  ${this.badge
                    ? html`<sh-badge class="thumbnail-badge" type="${this.badge}"></sh-badge>`
                    : ''}
                  <slot
                    name="indications"
                    class="indications"
                    @slotchange="${this._indicationsSlotObserver}"
                  ></slot>
                </div>
                <div class="info-slot-wrapper">
                  <slot name="info" class="info" @slotchange="${this._infoSlotObserver}"></slot>
                </div>
              </div>
            </div>
            <div class="label-wrapper">
              ${this.label || this.number ? this._getLabelOrNumber() : ''}
              <div>
                ${this.options
                  ? html` <sh-icon
                      button
                      icon="more"
                      class="options-icon"
                      id="optionsIcon"
                      @click="${this._showOptions}"
                    ></sh-icon>`
                  : ''}
                <sh-overlay
                  no-overlap
                  horizontal-align="auto"
                  scroll-action="refit"
                  autoFitOnAttach
                  vertical-align="auto"
                >
                  <sh-popover
                    id="popover"
                    class="options-popover"
                    @click="${this._handleMenuItemClick}"
                    @keyup="${this._handleMenuItemKeyup}"
                  >
                    <slot id="mainSlot" @slotchange="${this._mainSlotObserver}"></slot>
                  </sh-popover>
                </sh-overlay>
              </div>
            </div>
            <div class="icons-wrapper">
              <slot name="icons" id="icons"></slot>
            </div>
          `
        : html`
            <div class="loading-wrapper">
              <div
                class="loading-image loading"
                style="padding-top: calc(100% / (${this.aspectRatio}));"
              ></div>
              ${this.label || this.number
                ? this._getLabelOrNumber()
                : html` <div class="loading-text loading"></div>`}
            </div>
          `}
    `;
  }

  constructor() {
    super();
    this.src = '';
    this.value = '';
    this.aspectRatio = '1/1';
    this._emptyInfo = true;
    this._emptyIndications = true;
    this._emptyDataTypeIcons = true;
  }

  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    this.documentClickListener = this.documentClickActions.bind(this);
  }
  updated(changedProperties) {
    if (changedProperties.has('show')) {
      if (this.show) {
        this._showPopover();
      } else {
        this._hidePopover();
      }
    }
    const listOfProperties = [
      'label',
      'src',
      'number',
      'active',
      'focus',
      'selectable',
      'new',
      'badge',
      'checked',
      'indeterminate',
      'options',
      'condensed',
      'icon',
      'aspectRatio',
      'show',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  _getLabelOrNumber() {
    return html`
      <sh-text size="body-2" color="secondary" class="label" id="sh-thumbnail-text">
        ${this.number
          ? html`<b class="id-number"
              >${this.number} ${this.label && this.number ? html`•` : ''}
            </b>`
          : ''}
        ${this.label ? this.label : ''}
      </sh-text>
      ${this.label
        ? html`<sh-tooltip
            .label="${this.label}"
            delay
            placement="bottom-left"
            target="sh-thumbnail-text"
          ></sh-tooltip>`
        : ''}
    `;
  }

  documentClickActions(e) {
    // if either the thumbnail itself is not clicked,
    // or if the popover is showing mobile view
    // and if the options icon is not clicked,
    // then hide the popover.

    const optionsIcon = this.shadowRoot.querySelector('.options-icon');
    const notClickedHost = e.target !== this;
    const popoverIsMobile = this.shadowRoot.querySelector('.options-popover').mobile;
    const notClickedOptionsIcon = e.composedPath().indexOf(optionsIcon) === -1;
    if ((notClickedHost || popoverIsMobile) && notClickedOptionsIcon) {
      this.show = false;
    }
  }

  _handleMenuItemClick(e) {
    const clickedElement = e.target;
    const hasClickedOnMenuItem = clickedElement.tagName.toString().toLowerCase() === 'sh-menu-item';
    if (hasClickedOnMenuItem) {
      clickedElement.removeAttribute('active');
    }
  }

  _handleMenuItemKeyup(e) {
    const pressedElement = e.target;
    const pressedEnter = e.keyCode === KEYCODE.ENTER;
    const pressedSpace = e.keyCode === KEYCODE.SPACE;
    const hasPressedOnMenuItem = pressedElement.tagName.toString().toLowerCase() === 'sh-menu-item';
    if (hasPressedOnMenuItem && (pressedEnter || pressedSpace)) {
      pressedElement.click();
    }
  }

  _dataTypeSlotObserver(e) {
    const numberOfAssignedNodes = e.target.assignedNodes({
      flatten: true,
    }).length;
    this._emptyDataTypeIcons = numberOfAssignedNodes === 0;
  }

  _infoSlotObserver(e) {
    const numberOfAssignedNodes = e.target.assignedNodes({
      flatten: true,
    }).length;
    this._emptyInfo = numberOfAssignedNodes === 0;
  }

  _indicationsSlotObserver(e) {
    const numberOfAssignedNodes = e.target.assignedNodes({
      flatten: true,
    }).length;
    this._emptyIndications = numberOfAssignedNodes === 0;
  }

  _mainSlotObserver() {
    const numberOfMenuItems = this.querySelectorAll('sh-menu-item').length;
    this.options = numberOfMenuItems > 0;
    if (!this.options) {
      this.show = false;
    }
  }

  _showPopover() {
    const popover = this.shadowRoot.querySelector('sh-popover');
    const overlay = this.shadowRoot.querySelector('sh-overlay');

    popover.style.visibility = 'visible';
    overlay.open();
    popover.style.opacity = 1;
    document.body.addEventListener('click', this.documentClickListener);
  }

  _hidePopover() {
    const popover = this.shadowRoot.querySelector('sh-popover');
    const overlay = this.shadowRoot.querySelector('sh-overlay');

    popover.style.visibility = 'hidden';
    popover.style.opacity = 0;
    overlay.close();
    document.body.removeEventListener('click', this.documentClickListener);
  }

  _showOptions() {
    this.show = !this.show;
    dispatchCustomEvent(this, 'menu-clicked', this);
  }

  disconnectedCallback() {
    document.body.removeEventListener('click', this.documentClickListener);
    super.disconnectedCallback();
  }
}

customElements.define('sh-thumbnail', SHThumbnail);
